/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { docViewModes, AJSMetadataStatus, loadStatus, DOCUMENT_TYPE, pageUsageStatus } from "constants/enums";
import { getViewMode } from "selectors/viewModeSelectors";
import { isFirstPageLoaded, isInitialPageLoaded } from "selectors/pagedViewState";
import { getAnnotationFlagsUtil } from "utils/PageUtils";
import { isEqual } from "utils/lodashUtils";
import { getIsParentWebsiteScrolling, getShouldUseModernViewer, getSwitchPreviewState } from "selectors/appSelectors";
import { isFirstDCAPIRenditionLoaded } from "selectors/dcapiSelectors";
import { overlayEvents } from "overlays/OverlayEvents";
import { getMargin, getNumZoomOperations } from "selectors/zoom";
import { getViewPortWidth } from "selectors/documentViewSelectors";
import { getScrollbarSize } from "selectors/appUiStateSelectors";
import { getShouldRenderPersonalCommentsDropin, getShouldRenderCommentsDropin } from "selectors/overlayDropinSelectors";
import * as constants from "constants/constants";
import { getPageLoadStatus, getPageUsageStatus } from "selectors/pageSelectors";
import { isFirstExternalAPIRenditionLoaded } from "selectors/externalSelectors";
import { SWITCH_PREVIEW_STATE } from "constants/enums";

export const getPageHandle = (stores, index) =>
  stores.documentStore.pages[index] && stores.documentStore.pages[index].elementHandle;

export const getTotalPages = stores => stores.documentStore.pages.length;

export const getMaxTotalPageIndex = stores => stores.documentStore.maxTotalPagesIndex;

export const getDocInfo = stores => stores.documentStore.docInfo;

export const getPDFVersion = stores => stores.documentStore.pdfVersion;

export const getViewRotationAngle = stores => stores.documentStore.rotateState;

export const isRotated = stores => getViewRotationAngle(stores) === 90 || getViewRotationAngle(stores) === 270;

export const getNamedIcons = stores => stores.documentStore.namedIcons;

export const getNamedDests = stores => stores.documentStore.destinationNameToAction;

export const shouldLoadAcroformData = stores =>
  getShouldRenderAcroForm(stores) && (stores.documentStore.hasAcroFormFields || isInitialPageLoaded(stores));

export const getNamedDestActionsByName = (stores, destName) => {
  const destinationNameToActionMap = getNamedDests(stores);
  return destinationNameToActionMap ? destinationNameToActionMap.get(destName) : undefined;
};

export const getMaxPageWidth = stores =>
  !isRotated(stores) ? stores.documentStore.maxWidth : stores.documentStore.maxHeight;
export const getMaxPageHeight = stores =>
  !isRotated(stores) ? stores.documentStore.maxHeight : stores.documentStore.maxWidth;

export const getCurrMaxPageWidth = stores =>
  !isRotated(stores) ? stores.documentStore.currMaxWidth : stores.documentStore.currMaxHeight;
export const getCurrMaxPageHeight = stores =>
  !isRotated(stores) ? stores.documentStore.currMaxHeight : stores.documentStore.currMaxWidth;

export const getDocPermissions = stores => stores.documentStore.docPermissions;
export const getTitle = stores => stores.documentStore.title;
export const getPdfTitleAJS = stores => stores.documentStore.pdfTitleAJS;
export const getFileName = stores => stores.documentStore.fileName;
export const isPreloading = stores => stores.documentStore.isPreloading;
export const getPDFLanguage = stores => stores.documentStore.pdfLanguage;
export const dialogOpened = stores => stores.documentStore.dialogOpened;
export const getPagesInView = stores => stores.documentStore.oldPagesInView;
export const getPagesInDOM = stores => stores.documentStore.pagesInDOM;
export const getAJSMetadataStatus = stores => stores.documentStore.ajsMetadataStatus;
export const getSecondaryMetaDataStatus = stores => stores.documentStore.secondaryMetaDataStatus;
export const getShouldAutoHideToast = stores => stores.documentStore.shouldAutoHideToast;
export const getIsMouseOverToast = stores => stores.documentStore.isMouseOverToast;
export const getDisplayToast = stores => stores.documentStore.displayToast;
export const getFilenameForToast = stores => getFileName(stores) || getTitle(stores);
export const getNeedAppearancesStatus = stores => stores.documentStore.needAppearances;
export const getJSActionsAnalyticsData = stores => stores.documentStore.jsActionsAnalyticsData;
export const getIsFTPDF = stores => stores.documentStore.isFTPDF;
export const getIsTaggedPDF = stores => stores.documentStore.isTaggedPDF;

export const showToast = stores =>
  getDisplayToast(stores) && !!getFilenameForToast(stores) && isFirstPageLoaded(stores);

export const getFileDisplayName = stores => {
  if (stores.documentStore.displayDocTitle && getTitle(stores)) {
    return getTitle(stores);
  }
  return getFileExtension(stores) ? `${getFileName(stores)}.${getFileExtension(stores)}` : getFileName(stores);
};

export const getFileExtension = stores => stores.documentStore.fileExtension;

export const shouldStartRendering = stores => {
  const viewMode = getViewMode(stores);
  const totalPages = getTotalPages(stores);
  if (!getShouldShowPageLoader(stores) && totalPages > 0) {
    overlayEvents.trigger("pagesViewRendered", {});
  }
  switch (viewMode) {
    case docViewModes.CLASSIC_VIEW:
    case docViewModes.SINGLE_PAGE:
    case docViewModes.IN_PLACE:
    case docViewModes.FULL_SCREEN:
    case docViewModes.FIT_ONE_FULL_PAGE:
      return (
        (totalPages > 0 && (getIsLinearizedWorkflow(stores) || getIsDocOpenActionsCompleted(stores))) ||
        isFirstDCAPIRenditionLoaded(stores) ||
        isFirstExternalAPIRenditionLoaded(stores)
      );
    // The above check ensures we don't stop rendering for docopen actions to complete in case dcapi/external rendition is loaded for page
    case docViewModes.NON_PDF:
      return totalPages > 0;
    case docViewModes.LIQUID_MODE_VIEW:
    case docViewModes.NO_PREVIEW:
      return true;
    default:
      return false;
  }
};

export const getDocViewBgColor = stores => {
  if (isFullScreenMode(stores)) {
    return constants.FULL_SCREEN_MODE_BG_COLOR;
  }
  if (
    isFirstDCAPIRenditionLoaded(stores) ||
    isFirstExternalAPIRenditionLoaded(stores) ||
    getAJSMetadataStatus(stores) === AJSMetadataStatus.SUCCEEDED ||
    getSwitchPreviewState(stores) === SWITCH_PREVIEW_STATE.SWITCHED
  ) {
    return stores.documentStore.docViewBgColorAfterFileLoad;
  }
  return stores.documentStore.docViewBgColorBeforeFileLoad;
};
export const getIsDocumentLoadFailed = stores => stores.documentStore.documentLoadFailed;
export const getLoadFailReason = stores => stores.documentStore.documentLoadFailReason;
export const getShouldCreatePrintIframe = stores => stores.documentStore.shouldCreatePrintIframe;
export const getShouldRecomputePageSizes = stores => stores.documentStore.shouldRecomputePageSizes;
export const getUseReactVirtualized = stores => stores.documentStore.useReactVirtualized;
export const getShouldRenderAcroForm = stores => stores.documentStore.shouldRenderAcroForm;
export const getIsTilingEnabled = stores => stores.documentStore.isTilingEnabled;
export const isSinglePageMode = stores => stores.documentStore.viewMode === docViewModes.SINGLE_PAGE;
export const isPageByPageViewMode = stores => stores.documentStore.isPageByPageViewMode;
export const isContinuousViewMode = stores => stores.documentStore.isContinuousViewMode;
export const isTwoColumnViewMode = stores => stores.documentStore.isTwoColumnViewMode;
export const isCoverPageMode = stores => stores.documentStore.isCoverPageMode;
export const isViewWithScrolling = stores => stores.documentStore.isViewWithScrolling;
export const isFullScreenMode = stores => stores.documentStore.viewMode === docViewModes.FULL_SCREEN;
export const isFitOneFullPageMode = stores => stores.documentStore.viewMode === docViewModes.FIT_ONE_FULL_PAGE;
export const isFullScreenOrFitOnePageMode = stores => isFullScreenMode(stores) || isFitOneFullPageMode(stores);

export const getAssetURI = stores => stores.documentStore.assetURI;
export const getAlternateAssetURI = stores => stores.documentStore.alternateAssetUri;
export const isAlternateAssetURIAvailable = stores => stores.documentStore.isAlternateAssetURIAvailable;
export const getFileMetaData = stores => stores.documentStore.fileMetaData;
export const getFileSize = stores => stores.documentStore.fileSize;
export const getFirstRasterSize = stores => stores.documentStore.firstRasterSize;
export const getPagesCacheWindow = stores => stores.documentStore.pagesCacheWindow;
export const getScrollDirection = stores => stores.documentStore.scrollDirection;

export const getAnnotationFlags = stores => {
  const annotsFlagProvidedToDropin = stores.documentStore.selectivelyRenderAnnotsFlag;
  return annotsFlagProvidedToDropin || getAnnotationFlagsUtil();
};

export const getHiddenOCGs = stores => stores.documentStore.hiddenOCGs;

export const isHiddenOCG = (stores, ocgName) => {
  const ocgIndex = getHiddenOCGs(stores).findIndex(element => element === ocgName);
  return ocgIndex !== -1;
};

export const getShouldShowUnsupportedFieldAlert = stores =>
  stores.documentStore.shouldRenderAcroForm &&
  stores.documentStore.displayUnsupportedFeatureAlert &&
  stores.documentStore.showUnsupportedFieldAlert &&
  !getIsParentWebsiteScrolling(stores);

export const getDisplayOpenInDesktopOnUnsupportedNotification = stores =>
  stores.documentStore.displayOpenInDesktopOnUnsupportedNotification;

export const getDisplayPermissionToast = stores => stores.documentStore.displayPermissionToast;

export const viewDocumentProperties = stores => stores.documentStore.viewDocumentProperties;

export const isFilePasswordProtected = stores => stores.documentStore.isPasswordProtected;
export const isDocumentEncrypted = stores => stores.documentStore.isDocumentEncrypted;
export const doesDocumentHaveSigPerms = stores => stores.documentStore.documentHasSigPerms;
export const getIsEditingAllowed = stores => stores.documentStore.isEditingAllowed;
export const getHasAcroFormFields = stores => stores.documentStore.hasAcroFormFields;
export const getIsPortfolio = stores => stores.documentStore.isPortfolio;
export const getHasFASFields = stores => stores.documentStore.hasFASFields;

export const isClassicPDF = stores => {
  const viewMode = getViewMode(stores);
  if (
    viewMode === docViewModes.CLASSIC_VIEW ||
    viewMode === docViewModes.SINGLE_PAGE ||
    viewMode === docViewModes.IN_PLACE ||
    isFullScreenOrFitOnePageMode(stores)
  ) {
    return true;
  }
  return false;
};

export const isPDF = stores => isClassicPDF(stores);

export const getFontListOfPage = (stores, index) => stores.documentStore.pages[index].fontList;
export const getDocumentViewLeft = stores => {
  if (stores.documentStore.docViewRects && stores.documentStore.docViewRects.left !== "undefined") {
    return stores.documentStore.docViewRects.left;
  }
  return 0;
};

export const getShouldUnblockUIFeatures = stores => stores.documentStore.shouldUnblockUIFeatures;

export const getQuickToolsVisibility = stores => stores.documentStore.showQuickTools;
export const getSecondaryComponent = stores => stores.documentStore.secondaryComponent;
export const getSecondaryFlyoutComponent = stores => stores.documentStore.secondaryFlyoutComponent;
export const getColumnMaxWidth = (stores, columnIndex = 0) => {
  const margins = getMargin(stores);
  let retVal = getCurrMaxPageWidth(stores);
  if (margins.left !== "auto" && margins.right !== "auto") {
    const sumOfMargins = Number(margins.left + margins.right);
    retVal += sumOfMargins;
    retVal = Math.max(getViewPortWidth(stores), retVal);
  }

  if (isTwoColumnViewMode(stores)) {
    retVal += columnIndex ? constants.PAGES_VIEW_MARGIN_RIGHT : constants.PAGES_VIEW_MARGIN_LEFT;
    if (columnIndex % 2 === 0) {
      retVal += constants.TWO_COLUMN_MARGIN; // Only for left column index (i.e 0), add margin for TwoColumn
    }
  }
  let scrollbarSize = getScrollbarSize(stores);
  if (!scrollbarSize) {
    // Subtract 15px to the width to adjust for the additional 15px as added in _getFixedWidthValues function in AppController
    scrollbarSize = 15;
  }
  return retVal - (stores.zoom.getIsCommentsPanelVisible() || getShouldUseModernViewer(stores) ? scrollbarSize : 0);
};

export const getPermittedOperations = stores => stores.documentStore.permittedOperations;

export const getLatestHitTestResponse = stores => stores.documentStore.latestHitTestResponse;
export const getShouldShowPageLoader = stores => stores.documentStore.shouldShowPageLoader;
export const getIsXFA = stores => stores.documentStore.isXFA;
export const rhpVisibility = stores => stores.documentStore.showRhp;
export const getForceShowCommentingTools = stores => stores.documentStore.forceShowCommentingTools;
export const getShouldShowCommentingTools = stores =>
  getShouldRenderPersonalCommentsDropin(stores) ||
  getShouldRenderCommentsDropin(stores) ||
  getForceShowCommentingTools(stores);

export const getDocOpenActions = stores => stores.documentStore.docOpenActions;
export const getDocWillPrintActions = stores => stores.documentStore.docWillPrintActions;
export const getDocDidPrintActions = stores => stores.documentStore.docDidPrintActions;
export const getDocWillSaveActions = stores => stores.documentStore.docWillSaveActions;
export const getDocDidSaveActions = stores => stores.documentStore.docDidSaveActions;
export const getDocWillCloseActions = stores => stores.documentStore.docWillCloseActions;

export const getShouldTriggerCalculate = stores => stores.documentStore.shouldTriggerCalculate;

export const getIsLinearizedWorkflow = stores => stores.documentStore.isLinearizedWorkflow;

export const getFieldCalculationOrder = stores => stores.documentStore.fieldCalculationOrder;
export const getBaseUrl = stores => stores.documentStore.baseURL;
export const getPageLevelActions = stores => stores.documentStore.pageLevelActions || { jsAction: {}, nonJSAction: {} };
export const getLastOpenedPage = stores => stores.documentStore.lastOpenedPage;

export const getIsDocOpenActionsCompleted = stores =>
  getAJSMetadataStatus(stores) === AJSMetadataStatus.FAILED || stores.documentStore.docOpenActionsCompleted;

export const getShowStickyTools = stores => stores.documentStore.showStickyTools;

export const getBottomPanelHeight = stores => stores.documentStore.bottomPanelHeight;

export const getStickyToolsLoaded = stores => stores.documentStore.stickyToolsLoaded;

export const getUseProgressiveRendering = stores => stores.documentStore.useProgressiveRendering;

export const getProgressivelyRenderedPageStatus = (stores, pageIndex) => {
  const page = stores.documentStore.progressivelyRenderedPages[pageIndex] || {};
  return page.status;
};

export const getIsPageProgressivelyRendered = (stores, pageIndex) => {
  const status = getProgressivelyRenderedPageStatus(stores, pageIndex);
  const { BEING_LOADED, LOADED } = loadStatus;
  return status === BEING_LOADED || status === LOADED;
};

// progressive rendering parameters
export const getProgressiveRenderInterval = stores => stores.documentStore.progressiveRenderInterval;
export const getProgressiveRenderMaxInterval = stores => stores.documentStore.progressiveRenderMaxInterval;
export const getRenderIntervalMultiplier = stores => stores.documentStore.renderIntervalMultiplier;

export const getDocumentType = stores => {
  const { isXFA, isPortfolio, hasAcroFormFields } = stores.documentStore;
  // for XFA files, isXFA and hasAcroFormFields both are true so preference is given to isXFA
  // isXFA doesn't differentiate between Static and Dynamic XFA. Logging both as Static. Dynamic/Static XFA info has to be exposed from acrobatjs module
  if (isXFA) {
    return DOCUMENT_TYPE.STATIC_XFA;
  } else if (isPortfolio) {
    return DOCUMENT_TYPE.PORTFOLIO;
  } else if (hasAcroFormFields) {
    return DOCUMENT_TYPE.ACROFORM;
  }
  return DOCUMENT_TYPE.STANDARD;
};
export const getDetectedPDFLanguage = stores => stores.documentStore.detectedPDFLanguage;
export const getFileShareType = stores => stores.documentStore.fileShareType;
export const getFileStorageType = stores => stores.documentStore.fileStorageType;
export const getIsLpmEnabled = stores => stores.documentStore.perfMetadata.isLpmEnabled;
export const getPdfnowVerb = stores => stores.documentStore.perfMetadata.pdfnowVerb;
export const getExtensionDetected = stores => stores.documentStore.perfMetadata.extensionDetected;
export const shouldLogGetPageRenditionInfo = stores => stores.documentStore.logGetPageRenditionInfo;
export const getPageRenditionCallInfo = stores => ({
  ...stores.documentStore.getPageRenditionInfo,
  numPages: getTotalPages(stores)
});

export const getNumLoadedCachedPages = stores => {
  let count = 0;
  const start = 0,
    end = getTotalPages(stores) - 1;
  const pagesInView = getPagesInView(stores);
  for (let index = start; index < end; index++) {
    if (index < pagesInView.start || index > pagesInView.end) {
      if (getPageLoadStatus(stores, index) === loadStatus.LOADED) {
        count++;
      }
    }
  }
  return count;
};

export const getCacheBurstInfoForLogging = stores => ({
  ...stores.documentStore.cacheBurstInfo,
  numPages: getTotalPages(stores)
});

export const getCacheHitAndMissCount = stores => {
  let unusedLoadedPageCount = 0;
  const totalPages = getTotalPages(stores);
  for (let index = 0; index < totalPages; index++) {
    if (
      getPageLoadStatus(stores, index) === loadStatus.LOADED &&
      getPageUsageStatus(stores, index) === pageUsageStatus.NOT_USED
    ) {
      unusedLoadedPageCount++;
    }
  }
  const data = {
    cacheHitCount: stores.documentStore.cacheHitCount,
    cacheMissCount: stores.documentStore.cacheMissCount,
    unusedLoadedPageCount,
    totalPages
  };

  return data;
};

export const getInitialPageLoadTime = stores => stores.documentStore.initialPageLoadTime;
export const getIsFilteredDocOpenActionsPresent = stores => stores.documentStore.isFilteredDocOpenActionsPresent;
export const isReviewFile = (stores, env) =>
  (env.getEnvVar("app_identifier") === "dc-web-app" || env.getEnvVar("app_identifier") === "dc-web-pwa") &&
  getShouldRenderCommentsDropin(stores) &&
  !getShouldRenderPersonalCommentsDropin(stores);
export const getIsCropMode = stores => stores.documentStore.isCropMode;

export const getAssetRegion = stores => {
  const assetUri = getAssetURI(stores);
  try {
    const assetRegion = assetUri.split(":")[3];
    return assetRegion;
  } catch (err) {
    return "Error";
  }
};

export const getAssetPageCount = stores => stores.documentStore.assetPageCount;
export const getDocumentBottomSpace = stores => stores.documentStore.documentBottomSpace;

export const getIsImageCurrentSelection = stores => stores.documentStore.isImageCurrentSelection;

export const getCNPDFUpdateObjectCount = stores => stores.documentStore.updateObjectCount;

export const shouldTriggerDocumentViewUpdateEvent = (stores, previousDocumentViewInfo, newDocumentViewInfo) => {
  if (
    previousDocumentViewInfo &&
    (previousDocumentViewInfo.scrollHeight !== newDocumentViewInfo.scrollHeight ||
      previousDocumentViewInfo.scrollWidth !== newDocumentViewInfo.scrollWidth)
  ) {
    return true;
  }
  if (!stores.documentStore.triggerDocumentViewUpdateEvent) {
    return false;
  }
  if (previousDocumentViewInfo === null) {
    return true;
  }
  return !isEqual(previousDocumentViewInfo, newDocumentViewInfo);
};
